import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Brain, Heart, Award, Activity, Upload, Users, ExternalLink, RefreshCw, Loader2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import SafeResponsiveContainer from '../ui/SafeResponsiveContainer';
import AthleticAnalytics from '../AthleticAnalytics';
import { API } from '../../config/api';

export default function MeasurementsTab({
  childId,
  child,
  measurements,
  predictions,
  athleticTests,
  testSummary,
  coachRatings,
  latestRating,
  smhatAssessments,
  latestSMHAT
}) {
  const navigate = useNavigate();
  const [backfilling, setBackfilling] = useState(false);

  // Detect any measurement with a stale UK-WHO note or missing percentiles
  const hasStaleUKWHO = measurements.some(m =>
    m.uk_who_percentiles?.note ||
    m.uk_who_percentiles?.error ||
    (!m.uk_who_percentiles?.height_percentile && m.uk_who_percentiles?.height_percentile !== 0)
  );
  const staleNote = measurements.find(m => m.uk_who_percentiles?.note || m.uk_who_percentiles?.error)
    ?.uk_who_percentiles;

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/children/measurements/backfill-uk-who?child_id=${childId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'UK-WHO percentiles recalculated');
      // Force parent to reload by triggering navigate reload
      window.location.reload();
    } catch (err) {
      console.error('Backfill error:', err);
      toast.error(err.response?.data?.detail || 'Failed to recalculate UK-WHO data');
    } finally {
      setBackfilling(false);
    }
  };
  
  const chartData = measurements.map((m) => ({
    date: new Date(m.measurement_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
    age: m.age_years,
    height: m.standing_height,
    weight: m.weight,
    heightPercentile: m.uk_who_percentiles?.height_percentile,
    weightPercentile: m.uk_who_percentiles?.weight_percentile
  }));

  return (
    <div className="space-y-6">
      {/* Growth Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Height Chart */}
        <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6" data-testid="height-chart">
          <h3 className="text-xl font-bold text-white uppercase tracking-tight mb-6">Height Growth</h3>
          <div style={{ width: '100%', height: 300, minHeight: 300 }}>
            <SafeResponsiveContainer width="100%" height="100%" minHeight={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" stroke="#71717a" style={{ fontSize: 12, fontFamily: 'JetBrains Mono' }} />
                <YAxis stroke="#71717a" style={{ fontSize: 12, fontFamily: 'JetBrains Mono' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: 0 }}
                  labelStyle={{ color: '#fafafa', fontFamily: 'JetBrains Mono' }}
                />
                <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: 12 }} />
                <Line type="monotone" dataKey="height" stroke="#2563EB" strokeWidth={2} name="Height (cm)" dot={{ fill: '#2563EB', r: 4 }} />
                {predictions?.khamis_roche && !predictions.khamis_roche.error && (
                  <ReferenceLine y={predictions.khamis_roche.predicted_height_cm} stroke="#10B981" strokeDasharray="5 5" label={{ value: 'Target', fill: '#10B981', fontSize: 10 }} />
                )}
              </LineChart>
            </SafeResponsiveContainer>
          </div>
        </div>

        {/* Weight Chart */}
        <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6" data-testid="weight-chart">
          <h3 className="text-xl font-bold text-white uppercase tracking-tight mb-6">Weight Growth</h3>
          <div style={{ width: '100%', height: 300, minHeight: 300 }}>
            <SafeResponsiveContainer width="100%" height="100%" minHeight={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" stroke="#71717a" style={{ fontSize: 12, fontFamily: 'JetBrains Mono' }} />
                <YAxis stroke="#71717a" style={{ fontSize: 12, fontFamily: 'JetBrains Mono' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: 0 }}
                  labelStyle={{ color: '#fafafa', fontFamily: 'JetBrains Mono' }}
                />
                <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: 12 }} />
                <Line type="monotone" dataKey="weight" stroke="#10B981" strokeWidth={2} name="Weight (kg)" dot={{ fill: '#10B981', r: 4 }} />
              </LineChart>
            </SafeResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Measurements Table */}
      <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6" data-testid="measurements-table">
        <h3 className="text-xl font-bold text-white uppercase tracking-tight mb-6">Measurement History</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono">
            <thead className="border-b border-zinc-800">
              <tr className="text-left">
                <th className="pb-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Date</th>
                <th className="pb-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Age</th>
                <th className="pb-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Height (cm)</th>
                <th className="pb-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Weight (kg)</th>
                <th className="pb-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Height %ile</th>
                <th className="pb-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Weight %ile</th>
              </tr>
            </thead>
            <tbody>
              {measurements.map((m, idx) => (
                <tr key={m.id} className="border-b border-zinc-800/50" data-testid={`measurement-row-${idx}`}>
                  <td className="py-3 text-zinc-300">{new Date(m.measurement_date).toLocaleDateString()}</td>
                  <td className="py-3 text-zinc-300">{m.age_years.toFixed(1)}y</td>
                  <td className="py-3 text-white font-bold">{m.standing_height}</td>
                  <td className="py-3 text-white font-bold">{m.weight}</td>
                  <td className="py-3 text-[#02b5ff]">
                    {m.uk_who_percentiles?.height_percentile ? `${m.uk_who_percentiles.height_percentile.toFixed(1)}%` : 'N/A'}
                  </td>
                  <td className="py-3 text-[#02b5ff]">
                    {m.uk_who_percentiles?.weight_percentile ? `${m.uk_who_percentiles.weight_percentile.toFixed(1)}%` : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasStaleUKWHO && (
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded flex items-center justify-between gap-3" data-testid="api-warning">
            <div className="flex items-start gap-2 text-amber-300 text-xs font-mono">
              <span>⚠️</span>
              <span>
                {staleNote?.note || staleNote?.error || 'Some measurements are missing UK-WHO percentiles. Click Recalculate to fetch them.'}
              </span>
            </div>
            <Button
              size="sm"
              onClick={runBackfill}
              disabled={backfilling}
              className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30 text-xs"
              data-testid="backfill-uk-who-btn"
            >
              {backfilling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Recalculate
            </Button>
          </div>
        )}
      </div>

      {/* Coach Ratings Section */}
      <CoachRatingsSection 
        childId={childId}
        child={child}
        coachRatings={coachRatings}
        latestRating={latestRating}
        navigate={navigate}
      />

      {/* SMHAT Mental Health Assessment Section */}
      <SMHATSection
        childId={childId}
        latestSMHAT={latestSMHAT}
        smhatAssessments={smhatAssessments}
        navigate={navigate}
      />

      {/* Athletic Tests Section */}
      <AthleticTestsSection
        childId={childId}
        child={child}
        athleticTests={athleticTests}
        testSummary={testSummary}
        navigate={navigate}
      />
    </div>
  );
}

// Coach Ratings Sub-component
function CoachRatingsSection({ childId, child, coachRatings, latestRating, navigate }) {
  return (
    <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6" data-testid="coach-ratings-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Brain className="h-6 w-6 text-[#02b5ff]" />
          <h3 className="text-xl font-bold text-white uppercase tracking-tight">Coach Ratings</h3>
        </div>
        <Button
          data-testid="add-coach-rating-button"
          onClick={() => navigate(`/child/${childId}/add-coach-rating`)}
          className="h-10 px-6 bg-[#02b5ff] hover:bg-[#02b5ff] text-white font-bold uppercase tracking-wider text-xs hover:shadow-[0_0_30px_rgba(99,102,241,0.4)]"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Rating
        </Button>
      </div>

      {!latestRating ? (
        <div className="text-center py-12 border-t border-zinc-800" data-testid="no-coach-ratings">
          <Brain className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm mb-4">No coach ratings yet</p>
          <p className="text-zinc-600 text-xs font-mono">Add ratings to track psychological and social development</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-zinc-800 pt-6">
            <div className="bg-zinc-900/50 p-4">
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Psychological Avg</div>
              <div className="font-mono text-2xl font-bold text-[#02b5ff]">{latestRating.psychological_avg}/10</div>
            </div>
            <div className="bg-zinc-900/50 p-4">
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Social Avg</div>
              <div className="font-mono text-2xl font-bold text-[#02b5ff]">{latestRating.social_avg}/10</div>
            </div>
            <div className="bg-zinc-900/50 p-4">
              <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Overall Avg</div>
              <div className="font-mono text-2xl font-bold text-[#02b5ff]">{latestRating.overall_avg}/10</div>
            </div>
          </div>

          {/* Radar Chart */}
          {coachRatings.length > 0 && (
            <div className="bg-zinc-900/30 border border-zinc-800/50 p-6">
              <h4 className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-4">Latest Assessment Profile</h4>
              <div style={{ width: '100%', height: 400, minHeight: 400 }}>
                <SafeResponsiveContainer width="100%" height="100%" minHeight={400}>
                  <RadarChart data={[
                    { metric: 'Confidence', value: latestRating.confidence },
                    { metric: 'Commitment', value: latestRating.commitment },
                    { metric: 'Mental Control', value: latestRating.mental_control },
                    { metric: 'Concentration', value: latestRating.concentration },
                    { metric: 'Enjoying Training', value: latestRating.enjoying_training },
                    { metric: 'Helping Others', value: latestRating.helping_others },
                    { metric: 'Communication', value: latestRating.communication },
                    { metric: 'Involvement', value: latestRating.involvement }
                  ]}>
                    <PolarGrid stroke="#3f3f46" />
                    <PolarAngleAxis dataKey="metric" tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#71717a' }} />
                    <Radar name="Rating" dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.6} />
                  </RadarChart>
                </SafeResponsiveContainer>
              </div>
            </div>
          )}

          {/* Detailed Breakdown */}
          <div className="border-t border-zinc-800 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Psychological Traits */}
              <div className="bg-zinc-900/30 border border-zinc-800/50 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="h-5 w-5 text-[#02b5ff]" />
                  <h5 className="text-sm font-bold uppercase tracking-widest text-zinc-400">Psychological Traits</h5>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Confidence', value: latestRating.confidence },
                    { label: 'Commitment', value: latestRating.commitment },
                    { label: 'Mental Control', value: latestRating.mental_control },
                    { label: 'Concentration', value: latestRating.concentration }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm text-zinc-300">{item.label}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-purple-500 to-purple-400" 
                            style={{ width: `${item.value * 10}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-[#02b5ff] w-8">{item.value}/10</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Social Interaction */}
              <div className="bg-zinc-900/30 border border-zinc-800/50 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="h-5 w-5 text-[#02b5ff]" />
                  <h5 className="text-sm font-bold uppercase tracking-widest text-zinc-400">Social Interaction</h5>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Enjoying Training', value: latestRating.enjoying_training },
                    { label: 'Helping Others', value: latestRating.helping_others },
                    { label: 'Communication', value: latestRating.communication },
                    { label: 'Involvement', value: latestRating.involvement }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-sm text-zinc-300">{item.label}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#02b5ff] to-green-400" 
                            style={{ width: `${item.value * 10}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-[#02b5ff] w-8">{item.value}/10</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Rating Details */}
          <div className="bg-zinc-900/30 border border-zinc-800/50 p-4 text-xs text-zinc-500 font-mono">
            <div className="flex items-center justify-between">
              <span>Rated on: {new Date(latestRating.rating_date).toLocaleDateString()}</span>
              {latestRating.coach_name && <span>Coach: {latestRating.coach_name}</span>}
            </div>
            {latestRating.notes && (
              <div className="mt-3 pt-3 border-t border-zinc-800">
                <span className="text-zinc-400">Notes:</span> {latestRating.notes}
              </div>
            )}
          </div>

          {/* View All Ratings */}
          {coachRatings.length > 1 && (
            <div className="text-center pt-4">
              <p className="text-sm text-zinc-500">
                Total Ratings: <span className="text-white font-bold">{coachRatings.length}</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// SMHAT Section Sub-component
function SMHATSection({ childId, latestSMHAT, smhatAssessments, navigate }) {
  return (
    <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6" data-testid="smhat-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Heart className="h-6 w-6 text-pink-500" />
          <h3 className="text-xl font-bold text-white uppercase tracking-tight">Mental Health Assessment</h3>
        </div>
        <Button
          data-testid="take-smhat-button"
          onClick={() => navigate(`/child/${childId}/smhat-assessment`)}
          className="h-10 px-6 bg-pink-600 hover:bg-pink-500 text-white font-bold uppercase tracking-wider text-xs hover:shadow-[0_0_30px_rgba(236,72,153,0.4)]"
        >
          <Plus className="mr-2 h-4 w-4" />
          Take Assessment
        </Button>
      </div>

      {!latestSMHAT ? (
        <div className="text-center py-12 border-t border-zinc-800" data-testid="no-smhat">
          <Heart className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm mb-4">No mental health assessments yet</p>
          <p className="text-zinc-600 text-xs font-mono">IOC SMHAT-1 helps identify anxiety and mental health concerns</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Score Summary */}
          <div className={`border-t border-zinc-800 pt-6 rounded-xl p-6 ${
            latestSMHAT.exceeds_threshold 
              ? 'bg-[#ff025f]/20 border border-[#ff025f]/50' 
              : 'bg-[#02b5ff]/20 border border-[#02b5ff]/50'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Latest Score (IOC SMHAT-1)
                </div>
                <div className={`font-mono text-4xl font-bold ${
                  latestSMHAT.exceeds_threshold ? 'text-[#ff025f]' : 'text-[#02b5ff]'
                }`}>
                  {latestSMHAT.total_score} / 50
                </div>
              </div>
              <div className={`px-4 py-2 rounded-lg ${
                latestSMHAT.risk_level === 'high' ? 'bg-[#ff025f]' :
                latestSMHAT.risk_level === 'moderate' ? 'bg-[#02b5ff]' :
                'bg-[#02b5ff]'
              }`}>
                <div className="text-white font-bold text-sm uppercase">{latestSMHAT.risk_level} Risk</div>
              </div>
            </div>
            
            <div className={`text-sm leading-relaxed ${
              latestSMHAT.exceeds_threshold ? 'text-[#ff025f]' : 'text-[#02b5ff]'
            }`}>
              {latestSMHAT.interpretation}
            </div>
          </div>

          {/* Threshold Indicator */}
          {latestSMHAT.exceeds_threshold && (
            <div className="bg-[#ff025f]/20 border border-[#ff025f]/50 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="text-[#ff025f] text-2xl">⚠️</div>
                <div>
                  <h4 className="text-[#ff025f] font-bold mb-2">Anxiety Threshold Exceeded</h4>
                  <p className="text-[#ff025f]/80 text-sm leading-relaxed">
                    Score of {latestSMHAT.total_score} exceeds the threshold of 10. 
                    Consider discussing with a mental health professional, sports psychologist, or trusted adult. 
                    Your wellbeing is important and support is available.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Assessment Details */}
          <div className="bg-zinc-900/30 border border-zinc-800/50 p-5">
            <h4 className="text-sm font-bold uppercase tracking-widest text-zinc-400 mb-4">Assessment Details</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-zinc-500">Assessment Date:</span>
                <span className="text-zinc-300 ml-2 font-mono">
                  {new Date(latestSMHAT.assessment_date).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="text-zinc-500">Risk Level:</span>
                <span className={`ml-2 font-bold ${
                  latestSMHAT.risk_level === 'high' ? 'text-[#ff025f]' :
                  latestSMHAT.risk_level === 'moderate' ? 'text-[#02b5ff]' :
                  'text-[#02b5ff]'
                }`}>
                  {latestSMHAT.risk_level.toUpperCase()}
                </span>
              </div>
            </div>
            {latestSMHAT.notes && (
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <span className="text-zinc-500 text-xs">Notes:</span>
                <p className="text-zinc-300 text-sm mt-1">{latestSMHAT.notes}</p>
              </div>
            )}
          </div>

          {/* Assessment History */}
          {smhatAssessments.length > 1 && (
            <div className="text-center pt-4">
              <p className="text-sm text-zinc-500">
                Total Assessments: <span className="text-white font-bold">{smhatAssessments.length}</span>
              </p>
            </div>
          )}

          {/* Support Resources */}
          <div className="bg-[#02b5ff]/20 border border-[#02b5ff]/50 rounded-xl p-5">
            <h4 className="text-[#02b5ff] font-bold mb-3 text-sm">Mental Health Support Resources</h4>
            <ul className="text-[#02b5ff]/80 text-xs space-y-2">
              <li>• Speak with your coach, parent, or trusted adult</li>
              <li>• Contact a sports psychologist for professional support</li>
              <li>• NHS Mental Health Services: 111 (option 2)</li>
              <li>• Childline: 0800 1111 (free 24/7 support for under 19s)</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// Athletic Tests Section Sub-component
function AthleticTestsSection({ childId, child, athleticTests, testSummary, navigate }) {
  return (
    <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6" data-testid="athletic-tests-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Award className="h-6 w-6 text-[#02b5ff]" />
          <h3 className="text-xl font-bold text-white uppercase tracking-tight">Athletic Performance</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="manual-entry-button"
            onClick={() => navigate(`/child/${childId}/add-athletic-tests`)}
            variant="outline"
            className="h-10 px-4 border-emerald-500 text-emerald-400 hover:bg-emerald-500/10 font-bold uppercase tracking-wider text-xs"
          >
            <Plus className="mr-2 h-4 w-4" />
            Manual Entry
          </Button>
          <Button
            data-testid="upload-tests-button"
            onClick={() => navigate(`/child/${childId}/upload-athletic-tests`)}
            className="h-10 px-4 bg-[#02b5ff] hover:bg-[#02b5ff] text-white font-bold uppercase tracking-wider text-xs hover:shadow-[0_0_30px_rgba(168,85,247,0.4)]"
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload CSV
          </Button>
        </div>
      </div>

      {athleticTests.length === 0 ? (
        <div className="text-center py-12 border-t border-zinc-800" data-testid="no-athletic-tests">
          <Activity className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm mb-4">No athletic test results yet</p>
          <p className="text-zinc-600 text-xs font-mono">Click <strong className="text-emerald-400">Manual Entry</strong> to add a test, or upload a CSV file</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Performance Summary */}
          {testSummary && testSummary.total_tests > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-zinc-800 pt-6">
              <div className="bg-zinc-900/50 p-4">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Total Tests</div>
                <div className="font-mono text-2xl font-bold text-white">{testSummary.total_tests}</div>
              </div>
              <div className="bg-zinc-900/50 p-4">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Test Types</div>
                <div className="font-mono text-2xl font-bold text-white">{testSummary.unique_test_types}</div>
              </div>
              <div className="bg-zinc-900/50 p-4">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Avg Grade</div>
                <div className="font-mono text-2xl font-bold text-white">{testSummary.overall_average_grade}/5</div>
              </div>
            </div>
          )}

          {/* Performance Analytics with Age Benchmarks */}
          <div className="border-t border-zinc-800 pt-6">
            <AthleticAnalytics 
              childId={childId} 
              childName={child ? `${child.first_name} ${child.last_name}` : ''} 
            />
          </div>

          {/* Test Results by Type */}
          <div className="border-t border-zinc-800 pt-6">
            <h4 className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-4">Latest Results by Test</h4>
            <p className="text-xs text-zinc-600 mb-4">Click on a test to view detailed analytics</p>
            <div className="space-y-3">
              {testSummary?.test_summary?.map((test, idx) => {
                const isDiagnostic = !!(test.diagnostic_only || test.latest_grade == null);
                return (
                <div 
                  key={idx} 
                  className="bg-zinc-900/50 border border-zinc-800/50 p-4 cursor-pointer hover:bg-zinc-800/50 hover:border-[#02b5ff]/30 transition-all group" 
                  data-testid={`test-result-${idx}`}
                  onClick={() => {
                    const analyticsSection = document.querySelector('[data-testid="athletic-analytics"]');
                    if (analyticsSection) {
                      analyticsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-bold text-sm group-hover:text-[#02b5ff] transition-colors">{test.test_name}</span>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-white">{test.latest_value} {test.unit}</span>
                      {isDiagnostic ? (
                        <div
                          className="px-3 py-1 font-bold text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-400 border border-zinc-700"
                          title="Diagnostic metric — recorded for coach review, not graded against a benchmark."
                          data-testid={`grade-badge-diagnostic-${idx}`}
                        >
                          For information only
                        </div>
                      ) : (
                        <div className={`px-3 py-1 font-bold text-xs ${
                          test.latest_grade === 5 ? 'bg-[#02b5ff]/20 text-[#02b5ff]' :
                          test.latest_grade === 4 ? 'bg-[#02b5ff]/20 text-[#02b5ff]' :
                          test.latest_grade === 3 ? 'bg-[#02b5ff]/20 text-[#02b5ff]' :
                          test.latest_grade === 2 ? 'bg-[#ff025f]/20 text-[#ff025f]' :
                          'bg-[#ff025f]/20 text-[#ff025f]'
                        }`}>
                          GRADE {test.latest_grade}
                        </div>
                      )}
                      <ExternalLink className="h-4 w-4 text-zinc-500 group-hover:text-[#02b5ff] transition-colors" />
                    </div>
                  </div>
                  <div className="text-xs text-zinc-500 font-mono">
                    {new Date(test.latest_date).toLocaleDateString()} • Tests: {test.count}
                    {isDiagnostic ? '' : ` • Avg: ${test.average_grade ?? '—'}/5`}
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* All Test Results Details */}
          <div className="border-t border-zinc-800 pt-6">
            <details className="group">
              <summary className="cursor-pointer text-sm font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-300 flex items-center gap-2">
                <span>View All Test Details</span>
                <span className="transform group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="mt-4 space-y-4">
                {athleticTests.map((test, idx) => {
                  const isDiagnostic = !!(test.diagnostic_only || test.ungraded || test.grade == null);
                  return (
                  <div 
                    key={test.id} 
                    className="bg-zinc-900/30 border border-zinc-800/30 p-4 cursor-pointer hover:bg-zinc-800/30 hover:border-[#02b5ff]/20 transition-all" 
                    data-testid={`test-detail-${idx}`}
                    onClick={() => {
                      const analyticsSection = document.querySelector('[data-testid="athletic-analytics"]');
                      if (analyticsSection) {
                        analyticsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h5 className="text-white font-bold mb-1 flex items-center gap-2">
                          {test.test_name}
                          <ExternalLink className="h-3 w-3 text-zinc-500" />
                        </h5>
                        <p className="text-xs text-zinc-500 font-mono">
                          {new Date(test.test_date).toLocaleDateString()} • Age: {test.age_at_test}y • {test.age_group}
                        </p>
                      </div>
                      {isDiagnostic ? (
                        <div
                          className="px-3 py-1 font-bold text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-400 border border-zinc-700"
                          title="Diagnostic metric — recorded for coach review, not graded against a benchmark."
                        >
                          For information only
                        </div>
                      ) : (
                        <div className={`px-3 py-1 font-bold text-xs ${
                          test.grade === 5 ? 'bg-[#02b5ff]/20 text-[#02b5ff]' :
                          test.grade === 4 ? 'bg-[#02b5ff]/20 text-[#02b5ff]' :
                          test.grade === 3 ? 'bg-[#02b5ff]/20 text-[#02b5ff]' :
                          test.grade === 2 ? 'bg-[#ff025f]/20 text-[#ff025f]' :
                          'bg-[#ff025f]/20 text-[#ff025f]'
                        }`}>
                          GRADE {test.grade}/5
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <span className="text-xs text-zinc-500">Result:</span>
                        <span className="text-white font-mono ml-2">{test.result_value} {test.unit}</span>
                      </div>
                      <div>
                        <span className="text-xs text-zinc-500">Benchmark:</span>
                        <span className="text-zinc-400 font-mono text-xs ml-2">{isDiagnostic ? 'For information only' : test.benchmark_range}</span>
                      </div>
                    </div>
                    <div className="bg-zinc-950/50 border border-zinc-800/50 p-3">
                      <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">{isDiagnostic ? 'Note:' : 'Recommendation:'}</p>
                      <p className="text-sm text-zinc-300">{test.recommendation}</p>
                    </div>
                  </div>
                  );
                })}
              </div>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}
